import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { actions as composerPopoverActions } from '~/composer-popover';
import { ENTER_KEY } from '~/utils/contants';
import { selectWeekStartsMonday } from '~/user/selectors';
import { selectPostById } from '../../../../../post/slice';
import PostPreview from '../../../PostPreview';

import { isLastThirdDayOfWeek, isLastTwoDaysOfWeek } from './utils';

import PostContent from '../PostContent/PostContent';
import DraggablePostItem from '../DraggablePostItem/DraggablePostItem';
import { PostPreviewHoverWrapper, PostWrapper } from './styles';
import { usePostPermissions } from '../../../../../post/hooks';
import { PostEntity } from '../../../../../post/PostEntity';
import { filterPost } from '../../../../utils/filterPost';

export const PostItem = ({
  small,
  postId,
  isViewOnly,
  scheduledDay,
  inLastTwoRows,
  index,
  isDraggingOver,
  setOpenModal,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state?.calendar?.filters);
  const weekStartsOn = useSelector(selectWeekStartsMonday) ? 1 : 0;
  const post = filterPost({
    post: useSelector((state) => selectPostById(state, postId)),
    filters,
  });
  const { hasEditPermission } = usePostPermissions(post);
  const isSent = PostEntity.isSent(post);
  const canEdit = hasEditPermission && !isSent;

  // TODO: consider a post placeholder while loading
  if (!PostEntity.isPostEntity(post)) return <></>;

  const hasError = post.status === 'error';
  const dueAt = new Date(post?.dueAt * 1000);
  const pastDueAt = new Date() > dueAt;
  const shouldDisableDrag = hasError || !canEdit;

  const ariaLabel = `${
    post?.channel?.service
  } post scheduled for ${scheduledDay}, ${post?.formattedTime}${
    hasError ? ' with an error' : ''
  }`;

  const handleKeyDown = (event) => {
    if (event.keyCode === ENTER_KEY && canEdit) {
      event.preventDefault();
      dispatch(composerPopoverActions.handleEditClick(post.id));
    }
  };

  const handleOnClick = () => {
    if (canEdit) dispatch(composerPopoverActions.handleEditClick(post.id));
  };

  return (
    <DraggablePostItem
      postId={post?.id}
      index={index}
      shouldDisableDrag={shouldDisableDrag}
      isDraggingOver={isDraggingOver}
    >
      <PostPreviewHoverWrapper $canEdit={canEdit}>
        <PostPreview
          post={post}
          small={small}
          isViewOnly={isViewOnly}
          inLastTwoRows={inLastTwoRows}
          isEndOfWeek={isLastTwoDaysOfWeek(dueAt, { weekStartsOn })}
          isLastThirdDayOfWeek={isLastThirdDayOfWeek(dueAt, { weekStartsOn })}
          canEdit={canEdit}
          pastDueAt={pastDueAt}
          setOpenModal={setOpenModal}
        />
        <PostWrapper
          small={small}
          campaignColor={post.campaign?.color?.value}
          aria-label={ariaLabel}
          onKeyDown={handleKeyDown}
          onClick={handleOnClick}
          tabIndex="0"
          role={canEdit && 'button' ? 'button' : undefined}
          type={post.type}
          isBlueprint={post?.isBlueprint}
          needsApproval={post?.needsApproval}
        >
          <PostContent
            small={small}
            post={post}
            dueAt={dueAt}
            isViewOnly={isViewOnly}
            inLastTwoRows={inLastTwoRows}
          />
        </PostWrapper>
      </PostPreviewHoverWrapper>
    </DraggablePostItem>
  );
};

PostItem.propTypes = {
  small: PropTypes.bool,
  postId: PropTypes.string.isRequired,
  scheduledDay: PropTypes.string.isRequired,
  isViewOnly: PropTypes.bool,
  inLastTwoRows: PropTypes.bool,
  isDraggingOver: PropTypes.bool,
  index: PropTypes.number,
  setOpenModal: PropTypes.func,
};

PostItem.defaultProps = {
  small: false,
  inLastTwoRows: false,
  isViewOnly: false,
  index: 0,
  isDraggingOver: false,
  setOpenModal: null,
};
